// Radar Unificado Emoción–Cognición: superpone la capa CEP (emocional, rosa) y
// la CAD (académica, azul). La brecha entre polígonos evidencia la Discrepancia
// Emoción–Cognición; al pasar el mouse se muestra el score y el Z-score aprox.
import { WrappedTick, cleanAxisLabel } from "@/components/radarLabels";
import { useState, useEffect } from "react";
import {
  Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer, Tooltip, Legend,
} from "recharts";

const CEP_LABELS = {
  motivacion: "Motivación", ansiedad: "Ansiedad", confianza: "Confianza",
  perseverancia: "Perseverancia", atencion: "Atención", frustracion: "Frustración",
};
const CAD_LABELS = {
  aritmetica: "Aritmética", algebra: "Álgebra", geometria: "Geometría",
  razonamiento: "Razonamiento", resolucion: "Resolución",
};

// Z aprox. vs referencia teórica (μ=50, σ=15), coherente con el motor.
const zApprox = (v) => (v == null ? null : Math.round(((v - 50) / 15) * 100) / 100);

function UnifiedTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white rounded-lg shadow-lg border border-[#EEE0F0] p-2 text-xs">
      <p className="font-extrabold text-[#1F2547] mb-1">{label}</p>
      {payload.filter((p) => p.value != null).map((p) => (
        <p key={p.dataKey} style={{ color: p.color }} className="font-semibold">
          {p.name}: {p.value} <span className="text-[#6E7591]">(Z {zApprox(p.value)})</span>
        </p>
      ))}
    </div>
  );
}

export default function UnifiedProfileRadar({ cep = [], cad = [] }) {
  // Fix 2.1.1: Mismo patrón que ProfileRadar — retrasar render hasta mount.
  const [isMounted, setIsMounted] = useState(false);
  useEffect(() => { setIsMounted(true); }, []);

  // Eje = áreas académicas; capa emocional = nivel CEP global como anillo de
  // referencia (autopercepción), capa académica = desempeño real por área.
  const cepGlobal = cep.length ? Math.round(cep.reduce((a, x) => a + (x.score || 0), 0) / cep.length) : null;

  const axes = [
    ...cad.map((x) => ({ key: CAD_LABELS[x.area] || cleanAxisLabel(x.area), academico: x.score, emocional: cepGlobal })),
    ...cep.map((x) => ({ key: CEP_LABELS[x.dim] || cleanAxisLabel(x.dim), emocional: x.score, academico: null })),
  ];

  return (
    <div data-testid="radar-unificado">
      {isMounted && (
        <ResponsiveContainer width="100%" height={340}>
          <RadarChart data={axes} outerRadius="62%">
            <PolarGrid stroke="#E6E8F0" />
            <PolarAngleAxis dataKey="key" tick={(p) => <WrappedTick {...p} fontSize={11} fill="#41496a" maxChars={13} />} />
            <PolarRadiusAxis domain={[0, 100]} tick={{ fontSize: 9, fill: "#9AA1B4" }} angle={90} />
            <Radar name="Emocional (CEP)" dataKey="emocional" stroke="#A43790" fill="#A43790" fillOpacity={0.35} connectNulls />
            <Radar name="Académico (CAD)" dataKey="academico" stroke="#2C49A0" fill="#2C49A0" fillOpacity={0.45} connectNulls />
            <Tooltip content={<UnifiedTooltip />} />
            <Legend wrapperStyle={{ fontSize: 11, fontWeight: 700 }} />
          </RadarChart>
        </ResponsiveContainer>
      )}
      <p className="text-[11px] text-[#6E7591] text-center -mt-2">
        La zona donde el <b style={{ color: "#2C49A0" }}>azul (académico)</b> cae por debajo del <b style={{ color: "#A43790" }}>rosa (emocional)</b> revela la <b>brecha Emoción–Cognición</b>.
      </p>
    </div>
  );
}
